import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import api from "../lib/api";

/*
 * SellerPoliciesPanel — fetches and renders the seller's published trade
 * terms snapshot for a given invoice. Mirrors the chips that ship on the
 * invoice PDF + pack-list PDF (same source: GET /api/invoices/{iid}/policies).
 *
 * Renders as a collapsible card so it stays out of the way until the buyer
 * explicitly looks for the policy. Empty/missing values are hidden.
 */
const TERMS_LABEL = {
  NET_14: "Net 14", NET_30: "Net 30", NET_60: "Net 60", NET_90: "Net 90", PREPAID: "Prepaid",
};

function formatPct(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n <= 0) return "0% — no fee (cancel any time before pickup)";
  // 10 → "10%", 10.5 → "10.5%"
  return `${(Number.isInteger(n) ? n.toString() : n.toFixed(1))}% on orders cancelled after packing or unclaimed for 30 days`;
}

function row(label, value) {
  if (value == null || value === "" || value === false) return null;
  return { label, value };
}

function formatReturnPolicy(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) {
    return "All sales final — no returns or refunds post-delivery (damaged shipments still use Claims within the QI window).";
  }
  return `${n}-day return window from delivery. Damaged shipments use Claims within the QI window — those don't count against this window.`;
}

export default function SellerPoliciesPanel({ invoiceId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invoiceId || !open || policies) return;
    setLoading(true);
    api.get(`/invoices/${invoiceId}/policies`)
      .then(r => setPolicies(r.data))
      .catch(() => setPolicies({}))
      .finally(() => setLoading(false));
  }, [invoiceId, open, policies]);

  // Mirrors the chips that ship on the wholesale invoice PDF + pack list
  // PDF — keep parity so buyers see the same trade terms in-app, in the
  // PDF, and in the CSV export. Feb 2026 alignment:
  //   - Added "Return policy" row (sourced from policies.return_policy_days)
  //   - Removed "Damaged on arrival" row (implicit in QI window + footer)
  //   - Removed "Shipping options" row (chosen per-order on the PO itself)
  const rows = policies ? [
    row("Payment terms", policies.default_terms ? TERMS_LABEL[policies.default_terms] || policies.default_terms : null),
    row("Default lead time", policies.default_lead_days != null ? `${policies.default_lead_days} business day${policies.default_lead_days === 1 ? "" : "s"}` : null),
    row("Minimum order qty", policies.moq),
    row("Quality-inspection window", policies.qi_window_days != null ? `${policies.qi_window_days} day${policies.qi_window_days === 1 ? "" : "s"} from delivery` : null),
    row("Return window", policies.return_window_days != null ? `${policies.return_window_days} day${policies.return_window_days === 1 ? "" : "s"} from delivery` : null),
    row("Return policy", formatReturnPolicy(policies.return_policy_days)),
    row("Cancellation / restocking", formatPct(policies.restocking_fee_pct)),
    row("Warranty", policies.warranty),
    row("Ships from", policies.ships_from),
    row("Ships to", policies.ships_to),
    policies.offers_prebook ? row("Prebook (deposit + balance)", "Available for made-to-order items") : null,
    policies.dropship_eligible ? row("Drop-ship", "Eligible — buyer can request direct-to-customer fulfilment") : null,
  ].filter(Boolean) : [];

  return (
    <div className="rounded-xl border border-border" data-testid="seller-policies-panel">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="seller-policies-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-foreground/60">Seller's policies</div>
          <div className="text-[12px] text-foreground/55 mt-0.5">The exact terms stamped on your invoice + pack list PDF.</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-foreground/60"/> : <ChevronDown className="w-4 h-4 text-foreground/60"/>}
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3" data-testid="seller-policies-content">
          {loading && (
            <div className="flex items-center gap-2 py-4 text-foreground/55 text-sm">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading policies…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-foreground/55 py-2">No policies published.</div>
          )}
          {!loading && rows.length > 0 && (
            <div className="divide-y divide-border/60 text-sm">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[160px_1fr] gap-3 py-2">
                  <div className="text-foreground/60 text-[12.5px]">{r.label}</div>
                  <div className="text-[13px] text-foreground">{r.value}</div>
                </div>
              ))}
            </div>
          )}
          {!loading && policies?.terms_and_conditions && (
            <div className="mt-3 pt-3 border-t border-border/60">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/50 mb-1.5">Additional terms &amp; conditions</div>
              <div className="text-[12.5px] text-foreground/80 whitespace-pre-wrap leading-relaxed">
                {policies.terms_and_conditions}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
